import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useFirebase } from '../../contexts/FirebaseContext';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaSave } from 'react-icons/fa';

const EditProfile = ({ onClose }) => {
  const { currentUser } = useAuth();
  const { db } = useFirebase();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
    address: '',
    bio: ''
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        // Get additional user data from Firestore
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          // If user document doesn't exist, create it with default values
          const defaultData = {
            displayName: currentUser.displayName || '',
            email: currentUser.email || '',
            phone: '',
            address: '',
            bio: '',
            createdAt: new Date(),
            updatedAt: new Date()
          };
          await setDoc(userRef, defaultData);
          setFormData(defaultData);
        } else {
          const userData = userDoc.data();
          setFormData({
            displayName: currentUser.displayName || '',
            email: currentUser.email || '',
            phone: userData.phone || '',
            address: userData.address || '',
            bio: userData.bio || ''
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser, db]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Update display name in Firebase Auth
      if (formData.displayName !== currentUser.displayName) {
        await updateProfile(currentUser, {
          displayName: formData.displayName
        });
      }

      // Get reference to user document
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Check if document exists
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // Create new user document if it doesn't exist
        await setDoc(userRef, {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      } else {
        // Update existing document
        await updateDoc(userRef, {
          ...formData,
          updatedAt: new Date()
        });
      }

      toast.success('Profile updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6 relative">
        <h2 className="text-2xl font-bold text-white mb-6">Edit Profile</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="flex items-center gap-2 text-gray-300 text-sm mb-1">
              <FaUser />
              Display Name
            </label>
            <input
              type="text"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your name"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-gray-300 text-sm mb-1">
              <FaEnvelope />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              disabled
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400
                       cursor-not-allowed"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-gray-300 text-sm mb-1">
              <FaPhone />
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your phone number"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-gray-300 text-sm mb-1">
              <FaMapMarkerAlt />
              Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your address"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-gray-300 text-sm mb-1">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Tell us about yourself"
            />
          </div>

          <div className="flex gap-4 mt-6">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium
                       hover:bg-blue-700 transition-colors duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2"
            >
              <FaSave className={loading ? 'animate-spin' : ''} />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2.5 bg-gray-800 text-gray-300 rounded-lg font-medium
                       hover:bg-gray-700 transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile; 